package com.milta.feature.optimizationquota.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.optimizationquota.domain.entity.AdminAccountOptimizationQuota
import com.milta.feature.optimizationquota.domain.usecase.GetAccountOptimizationHistoryUseCase
import com.milta.feature.optimizationquota.domain.usecase.GetAccountOptimizationQuotaUseCase
import com.milta.feature.optimizationquota.domain.usecase.ReplaceRemainingOptimizationsUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

/** Server cap on a replaced remaining count (OptimizationQuotaSettings.MaxRemainingOverride). */
const val MAX_REMAINING_OVERRIDE = 100_000

/** Digits kept in a numeric admin field: never overflows Int. */
internal const val MAX_INPUT_DIGITS = 9

/** "Nombre restant remplacé : [from] → [to]" banner, right after a replacement (R5). */
data class RemainingReplacement(val from: Int, val to: Int)

data class AdminAccountQuotaState(
    val isLoading: Boolean = true,
    /** "not_found" | "server_error" | "offline". */
    val error: String? = null,
    val data: AdminAccountOptimizationQuota? = null,
    val remainingInput: String = "",
    /** The field is empty — nothing sent. */
    val remainingInputInvalid: Boolean = false,
    /** Above [MAX_REMAINING_OVERRIDE] (the server's cap) — nothing sent. */
    val remainingInputTooLarge: Boolean = false,
    val isReplacing: Boolean = false,
    val lastReplacement: RemainingReplacement? = null,
    /** "validation" | "server_error" | "offline". */
    val replaceError: String? = null,
    val history: OptimizationHistoryState = OptimizationHistoryState(),
)

sealed interface AdminAccountQuotaIntent {
    data object Load : AdminAccountQuotaIntent
    data object LoadMoreHistory : AdminAccountQuotaIntent
    data class UpdateRemainingInput(val value: String) : AdminAccountQuotaIntent
    data object ReplaceRemaining : AdminAccountQuotaIntent
}

/**
 * MIL-97 — Admin "Fiche utilisateur" (A2): identity, account kind, the account's own counter (R1),
 * "Remplacer le nombre restant" for the current period only (R5) and the paged history (R12, R13).
 * Replacing the remaining count does not touch the history, so it is not reloaded.
 */
class AdminAccountQuotaViewModel(
    private val accountId: String,
    private val getAccountQuota: GetAccountOptimizationQuotaUseCase,
    private val getAccountHistory: GetAccountOptimizationHistoryUseCase,
    private val replaceRemaining: ReplaceRemainingOptimizationsUseCase,
) : MviViewModel<AdminAccountQuotaState, AdminAccountQuotaIntent>(AdminAccountQuotaState()) {

    init {
        onIntent(AdminAccountQuotaIntent.Load)
    }

    override fun onIntent(intent: AdminAccountQuotaIntent) {
        when (intent) {
            AdminAccountQuotaIntent.Load -> load()
            AdminAccountQuotaIntent.LoadMoreHistory -> loadMore()
            is AdminAccountQuotaIntent.UpdateRemainingInput -> setState {
                copy(
                    // Capped in length so an oversized entry never overflows Int.
                    remainingInput = intent.value.filter { it.isDigit() }.take(MAX_INPUT_DIGITS),
                    remainingInputInvalid = false,
                    remainingInputTooLarge = false,
                    replaceError = null,
                )
            }
            AdminAccountQuotaIntent.ReplaceRemaining -> replace()
        }
    }

    /** Bumped by [load]: a page answer from before a reset is ignored (no skipped page). */
    private var historyGeneration = 0
    private var historyJob: Job? = null

    private fun load() {
        historyGeneration++
        historyJob?.cancel()
        setState { copy(isLoading = true, error = null, history = OptimizationHistoryState()) }
        scope.launch {
            getAccountQuota(accountId).fold(
                onSuccess = { data -> setState { copy(isLoading = false, data = data) } },
                onFailure = { e -> setState { copy(isLoading = false, error = errorCode(e)) } },
            )
        }
        loadMore()
    }

    private fun loadMore() {
        val history = currentState.history
        if (history.isLoadingPage || !history.hasMore) return
        setState { copy(history = history.copy(isLoadingPage = true, error = null)) }
        val generation = historyGeneration
        historyJob = scope.launch {
            val result = getAccountHistory(accountId, history.nextPage, OPTIMIZATION_HISTORY_PAGE_SIZE)
            if (generation != historyGeneration) return@launch
            result.fold(
                onSuccess = { page -> setState { copy(history = this.history.withPage(page)) } },
                onFailure = { e -> setState { copy(history = this.history.withError(e)) } },
            )
        }
    }

    private fun replace() {
        val current = currentState.data ?: return
        if (currentState.isReplacing) return
        val value = currentState.remainingInput.toIntOrNull()
        if (value == null) {
            setState { copy(remainingInputInvalid = true) }
            return
        }
        if (value > MAX_REMAINING_OVERRIDE) {
            setState { copy(remainingInputTooLarge = true) }
            return
        }
        val previous = current.quota.remaining
        setState { copy(isReplacing = true, replaceError = null, lastReplacement = null) }
        scope.launch {
            replaceRemaining(accountId, value).fold(
                onSuccess = { quota ->
                    setState {
                        copy(
                            isReplacing = false,
                            data = data?.copy(quota = quota),
                            remainingInput = "",
                            lastReplacement = RemainingReplacement(previous, quota.remaining),
                        )
                    }
                },
                onFailure = { e ->
                    val code = if (e is ApiException && e.statusCode == HTTP_BAD_REQUEST) "validation" else errorCode(e)
                    setState { copy(isReplacing = false, replaceError = code) }
                },
            )
        }
    }

    private fun errorCode(e: Throwable) = when {
        e is ApiException && e.statusCode == HTTP_NOT_FOUND -> "not_found"
        e is ApiException -> "server_error"
        else -> "offline"
    }

    private companion object {
        const val HTTP_BAD_REQUEST = 400
        const val HTTP_NOT_FOUND = 404
    }
}
